/**
 * Field definitions for Basic and Comprehensive VSME modules
 *
 * Basic module = Required fields (mandatory for any report)
 * Comprehensive module = All fields (required + optional)
 *
 * These definitions are derived from the completion calculation logic
 * in the frontend slices and backend services.
 */

export type TabName = 'general' | 'environment' | 'social' | 'governance' | 'sustainability-story';

export interface FieldDefinition {
  fieldKey: string;
  label: string;
  isBasic: boolean;  // true = required for Basic module
  cardId: string;
  isArray?: boolean;  // For array fields - check if array has at least one item
  isBoolean?: boolean;  // For boolean fields that must be explicitly set
  conditionalOn?: {  // Field only required when another field has specific value
    field: string;
    value: any;
  };
  checkType?: 'numeric' | 'text' | 'boolean' | 'array' | 'arrayOrFields' | 'primarySiteAddress';  // How to validate
}

export interface CardDefinition {
  cardId: string;
  cardName: string;
  tab: TabName;
  fields: FieldDefinition[];
  hidden?: boolean;  // If true, card is not rendered as top-level in UI (nested under parent)
}

export const FIELD_DEFINITIONS: CardDefinition[] = [
  // ==================== GENERAL TAB ====================
  {
    cardId: "generalInfo",
    cardName: "General Information",
    tab: "general",
    fields: [
      { fieldKey: "companyName", label: "Company name", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "siteAddresses", label: "Site addresses", isBasic: true, cardId: "generalInfo", checkType: "primarySiteAddress" },
      { fieldKey: "numberOfSites", label: "Number of sites", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "legalForm", label: "Legal form", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "ownershipStructure", label: "Ownership structure", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "naceSector", label: "NACE sector", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "reportingCurrency", label: "Reporting currency", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "annualRevenueRange", label: "Annual revenue range", isBasic: true, cardId: "generalInfo", checkType: "text" },
      { fieldKey: "netTurnover", label: "Net turnover", isBasic: true, cardId: "generalInfo", checkType: "numeric" },
      { fieldKey: "balanceSheetTotal", label: "Balance sheet total", isBasic: true, cardId: "generalInfo", checkType: "numeric" },
      { fieldKey: "totalEmployees", label: "Total employees", isBasic: true, cardId: "generalInfo", checkType: "numeric" },
      { fieldKey: "maleEmployees", label: "Male employees", isBasic: true, cardId: "generalInfo", checkType: "numeric" },
      { fieldKey: "femaleEmployees", label: "Female employees", isBasic: true, cardId: "generalInfo", checkType: "numeric" },
      // Optional fields (Comprehensive only)
      { fieldKey: "homeworkingPercent", label: "Homeworking percentage", isBasic: false, cardId: "generalInfo", checkType: "numeric" },
     // { fieldKey: "legalFormSubtype", label: "Legal form subtype", isBasic: false, cardId: "generalInfo", checkType: "text" },
    ],
  },
  {
    cardId: "sustainability",
    cardName: "Sustainability Policies",
    tab: "general",
    fields: [
      { fieldKey: "descriptionOfActivities", label: "Description of main activities, products, services, and market segments", isBasic: true, cardId: "sustainability", checkType: "text" },
      { fieldKey: "sustainabilityCertifications", label: "Sustainability certifications (certificate document required)", isBasic: false, cardId: "sustainability", isArray: true, checkType: "array" },
      { fieldKey: "policiesByTopic", label: "Sustainability policy topics (optional)", isBasic: false, cardId: "sustainability", checkType: "array" },
    ],
  },
  // ==================== ENVIRONMENT TAB ====================
  {
    cardId: "carbonEmissions",
    cardName: "Carbon Emissions",
    tab: "environment",
    fields: [
      // At least one of these array fields must have entries
      { fieldKey: "carbonEntries", label: "At least one carbon entry (fuel, electricity, or refrigerant)", isBasic: true, cardId: "carbonEmissions", checkType: "arrayOrFields" },
    ],
  },
  {
    cardId: "pollution",
    cardName: "Pollution",
    tab: "environment",
    fields: [
      { fieldKey: "applicability", label: "Pollution reporting applicability", isBasic: true, cardId: "pollution", checkType: "text" },
    ],
  },
  {
    cardId: "biodiversity",
    cardName: "Biodiversity",
    tab: "environment",
    fields: [
      { fieldKey: "sitesInSensitiveAreas", label: "Sites in/near sensitive areas (count)", isBasic: true, cardId: "biodiversity", checkType: "numeric" },
      { fieldKey: "totalLandUse", label: "Total land use (hectares)", isBasic: true, cardId: "biodiversity", checkType: "numeric" },
      // Optional fields
      { fieldKey: "areaOfSensitiveSites", label: "Area of sensitive sites", isBasic: false, cardId: "biodiversity", checkType: "numeric" },
      { fieldKey: "sealedArea", label: "Sealed/covered area", isBasic: false, cardId: "biodiversity", checkType: "numeric" },
      { fieldKey: "natureOrientedOnSite", label: "Nature-oriented area on-site", isBasic: false, cardId: "biodiversity", checkType: "numeric" },
      { fieldKey: "natureOrientedOffSite", label: "Nature-oriented area off-site", isBasic: false, cardId: "biodiversity", checkType: "numeric" },
    ],
  },
  {
    cardId: "water",
    cardName: "Water",
    tab: "environment",
    fields: [
      { fieldKey: "annualConsumption", label: "Annual water consumption (m3)", isBasic: true, cardId: "water", checkType: "numeric" },
      // Optional fields
      { fieldKey: "totalWithdrawal", label: "Total water withdrawal (m3)", isBasic: false, cardId: "water", checkType: "numeric" },
      { fieldKey: "withdrawalInStressAreas", label: "Withdrawal in water stress areas", isBasic: false, cardId: "water", checkType: "numeric" },
      { fieldKey: "hasConsumptionProcess", label: "Has significant water-consuming processes", isBasic: false, cardId: "water", checkType: "boolean" },
      { fieldKey: "waterSource", label: "Main water source", isBasic: false, cardId: "water", checkType: "text" },
    ],
  },
  {
    cardId: "waste",
    cardName: "Waste",
    tab: "environment",
    fields: [
      { fieldKey: "appliesCircularEconomy", label: "Circular economy principles indicated", isBasic: true, cardId: "waste", checkType: "boolean" },
      { fieldKey: "circularEconomyDescription", label: "Circular economy description", isBasic: true, cardId: "waste", checkType: "text", conditionalOn: { field: "appliesCircularEconomy", value: true } },
      { fieldKey: "nonHazardousWaste", label: "Non-hazardous waste (tonnes)", isBasic: false, cardId: "waste", checkType: "numeric" },
      { fieldKey: "hazardousWaste", label: "Hazardous waste (tonnes)", isBasic: false, cardId: "waste", checkType: "numeric" },
      // Optional fields
      { fieldKey: "wasteDiverted", label: "Waste diverted to recycling/reuse", isBasic: false, cardId: "waste", checkType: "numeric" },
      { fieldKey: "tracksMaterials", label: "Tracks material inputs", isBasic: false, cardId: "waste", checkType: "boolean" },
      { fieldKey: "materialsDescription", label: "Materials mass-flow description", isBasic: false, cardId: "waste", checkType: "text", conditionalOn: { field: "tracksMaterials", value: true } },
    ],
  },
// ==================== SCOPE 3 CARDS (NESTED UNDER CARBON EMISSIONS) ====================
  
  {
    cardId: "businessTravel",
    cardName: "Business Travel (Scope 3)",
    tab: "environment",
    hidden: true,
    fields: [
      { fieldKey: "businessTravelEntries", label: "At least one business travel entry", isBasic: false, cardId: "businessTravel", checkType: "array" },
    ],
  },
  {
    cardId: "employeeCommuting",
    cardName: "Employee Commuting (Scope 3)",
    tab: "environment",
    hidden: true,
    fields: [
      { fieldKey: "employeeCommutingEntries", label: "At least one employee commuting entry", isBasic: false, cardId: "employeeCommuting", checkType: "array" },
    ],
  },
  {
    cardId: "freight",
    cardName: "Freight & Logistics (Scope 3)",
    tab: "environment",
    hidden: true,
    fields: [
      { fieldKey: "freightEntries", label: "At least one freight entry", isBasic: false, cardId: "freight", checkType: "array" },
    ],
  },
  // ==================== SOCIAL TAB ====================
  {
    cardId: "employeeSupport",
    cardName: "Employee Support",
    tab: "social",
    fields: [
      // B8: Workforce Basics
      { fieldKey: "permanentEmployees", label: "Permanent employees count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "temporaryEmployees", label: "Temporary employees count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "femaleEmployees", label: "Female employees count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "maleEmployees", label: "Male employees count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      // B9: Health & Safety
      { fieldKey: "recordableAccidents", label: "Recordable accidents count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "workRelatedFatalities", label: "Work-related fatalities count", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      // B10: Pay, Bargaining & Training
      { fieldKey: "minimumWageCompliance", label: "Minimum wage compliance (Yes/No)", isBasic: true, cardId: "employeeSupport", checkType: "boolean" },
      { fieldKey: "collectiveBargainingPercent", label: "Collective bargaining coverage %", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "trainingHoursFemale", label: "Training hours (female)", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "trainingHoursMale", label: "Training hours (male)", isBasic: true, cardId: "employeeSupport", checkType: "numeric" },
      // Optional fields
      { fieldKey: "employeesByCountry", label: "Employees by country", isBasic: false, cardId: "employeeSupport", checkType: "text" },
      { fieldKey: "turnoverRate", label: "Employee turnover rate", isBasic: false, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "accidentRate", label: "Accident rate", isBasic: false, cardId: "employeeSupport", checkType: "numeric" },
      { fieldKey: "genderPayGapPercent", label: "Gender pay gap %", isBasic: false, cardId: "employeeSupport", checkType: "numeric" },
    ],
  },
  {
    cardId: "humanRights",
    cardName: "Human Rights",
    tab: "social",
    fields: [
      { fieldKey: "hasHumanRightsPolicy", label: "Human rights policy (Yes/No)", isBasic: true, cardId: "humanRights", checkType: "boolean" },
      { fieldKey: "hasGrievanceMechanism", label: "Grievance mechanism (Yes/No)", isBasic: true, cardId: "humanRights", checkType: "boolean" },
      { fieldKey: "workforceIncidents", label: "Workforce incidents reported (Yes/No)", isBasic: true, cardId: "humanRights", checkType: "boolean" },
      { fieldKey: "valueChainIncidents", label: "Value chain incidents reported (Yes/No)", isBasic: true, cardId: "humanRights", checkType: "boolean" },
      // Optional fields
      { fieldKey: "policyCovers", label: "Policy coverage areas", isBasic: false, cardId: "humanRights", checkType: "array" },
      { fieldKey: "workforceIncidentsDescription", label: "Workforce incidents description", isBasic: false, cardId: "humanRights", checkType: "text", conditionalOn: { field: "workforceIncidents", value: true } },
      { fieldKey: "valueChainIncidentsDescription", label: "Value chain incidents description", isBasic: false, cardId: "humanRights", checkType: "text", conditionalOn: { field: "valueChainIncidents", value: true } },
    ],
  },
  {
    cardId: "diversityInclusion",
    cardName: "Diversity & Inclusion",
    tab: "social",
    fields: [
      // All optional - no required fields for this card
      { fieldKey: "femaleManagers", label: "Female managers count", isBasic: false, cardId: "diversityInclusion", checkType: "numeric" },
      { fieldKey: "maleManagers", label: "Male managers count", isBasic: false, cardId: "diversityInclusion", checkType: "numeric" },
      { fieldKey: "selfEmployedWorkers", label: "Self-employed workers count", isBasic: false, cardId: "diversityInclusion", checkType: "numeric" },
      { fieldKey: "temporaryAgencyWorkers", label: "Temporary agency workers count", isBasic: false, cardId: "diversityInclusion", checkType: "numeric" },
      { fieldKey: "diversityPolicies", label: "Diversity policies description", isBasic: false, cardId: "diversityInclusion", checkType: "text" },
    ],
  },
  {
    cardId: "communityContribution",
    cardName: "Community Contribution",
    tab: "social",
    fields: [
      // All optional - no required fields for this card
      { fieldKey: "communityPrograms", label: "Community programs description", isBasic: false, cardId: "communityContribution", checkType: "text" },
      { fieldKey: "charitableGivingAmount", label: "Charitable giving amount", isBasic: false, cardId: "communityContribution", checkType: "numeric" },
      { fieldKey: "volunteerHours", label: "Volunteer hours", isBasic: false, cardId: "communityContribution", checkType: "numeric" },
      { fieldKey: "measurableOutcomes", label: "Measurable outcomes", isBasic: false, cardId: "communityContribution", checkType: "text" },
    ],
  },

  // ==================== GOVERNANCE TAB ====================
  {
    cardId: "ethics",
    cardName: "Ethics",
    tab: "governance",
    fields: [
      { fieldKey: "hasConvictions", label: "Convictions/fines (Yes/No)", isBasic: true, cardId: "ethics", checkType: "boolean" },
      { fieldKey: "convictionsCount", label: "Number of convictions", isBasic: true, cardId: "ethics", checkType: "numeric", conditionalOn: { field: "hasConvictions", value: true } },
      { fieldKey: "finesAmount", label: "Total fines amount", isBasic: true, cardId: "ethics", checkType: "numeric", conditionalOn: { field: "hasConvictions", value: true } },
      // Optional fields
      { fieldKey: "antiCorruptionPolicy", label: "Anti-corruption policy (Yes/No)", isBasic: false, cardId: "ethics", checkType: "boolean" },
      { fieldKey: "whistleblowerMechanism", label: "Whistleblower mechanism (Yes/No)", isBasic: false, cardId: "ethics", checkType: "boolean" },
    ],
  },
  {
    cardId: "transparency",
    cardName: "Transparency",
    tab: "governance",
    fields: [
      { fieldKey: "hasFinancialStatements", label: "Prepares financial statements (Yes/No)", isBasic: true, cardId: "transparency", checkType: "boolean" },
      { fieldKey: "financialStatementsFrequency", label: "Financial statements frequency", isBasic: true, cardId: "transparency", checkType: "text", conditionalOn: { field: "hasFinancialStatements", value: true } },
      { fieldKey: "stakeholderCommunication", label: "Stakeholder communication description", isBasic: true, cardId: "transparency", checkType: "text" },
      // Optional fields
      { fieldKey: "hasExternalAudit", label: "External audit (Yes/No)", isBasic: false, cardId: "transparency", checkType: "boolean" },
      { fieldKey: "supportingDocumentsLinks", label: "Supporting documents links", isBasic: false, cardId: "transparency", checkType: "text" },
    ],
  },
  {
    cardId: "leadership",
    cardName: "Leadership",
    tab: "governance",
    fields: [
      { fieldKey: "hasGovernanceBody", label: "Has governance body (Yes/No)", isBasic: true, cardId: "leadership", checkType: "boolean" },
      // Optional fields
      { fieldKey: "femaleBoardMembers", label: "Female board members count", isBasic: false, cardId: "leadership", checkType: "numeric", conditionalOn: { field: "hasGovernanceBody", value: true } },
      { fieldKey: "maleBoardMembers", label: "Male board members count", isBasic: false, cardId: "leadership", checkType: "numeric", conditionalOn: { field: "hasGovernanceBody", value: true } },
      { fieldKey: "boardMembersTotal", label: "Total board members", isBasic: false, cardId: "leadership", checkType: "numeric", conditionalOn: { field: "hasGovernanceBody", value: true } },
      { fieldKey: "boardDiversityFemalePercent", label: "Board diversity (female %)", isBasic: false, cardId: "leadership", checkType: "numeric", conditionalOn: { field: "hasGovernanceBody", value: true } },
    ],
  },
  {
    cardId: "riskManagement",
    cardName: "Climate risk management",
    tab: "environment",
    fields: [
      { fieldKey: "climateRisksIdentified", label: "Climate risks identified (Yes/No)", isBasic: true, cardId: "riskManagement", checkType: "boolean" },
      // Optional fields
      { fieldKey: "mainHazards", label: "Main hazards description", isBasic: false, cardId: "riskManagement", checkType: "text", conditionalOn: { field: "climateRisksIdentified", value: true } },
      { fieldKey: "riskAssessmentProcess", label: "Risk assessment process", isBasic: false, cardId: "riskManagement", checkType: "text", conditionalOn: { field: "climateRisksIdentified", value: true } },
      { fieldKey: "timeHorizon", label: "Time horizons considered", isBasic: false, cardId: "riskManagement", checkType: "array", conditionalOn: { field: "climateRisksIdentified", value: true } },
      { fieldKey: "adaptationActions", label: "Adaptation actions", isBasic: false, cardId: "riskManagement", checkType: "text", conditionalOn: { field: "climateRisksIdentified", value: true } },
      { fieldKey: "riskLevel", label: "Risk level assessment", isBasic: false, cardId: "riskManagement", checkType: "text", conditionalOn: { field: "climateRisksIdentified", value: true } },
      { fieldKey: "potentialEffects", label: "Potential effects description", isBasic: false, cardId: "riskManagement", checkType: "text", conditionalOn: { field: "climateRisksIdentified", value: true } },
    ],
  },
  {
    cardId: "accountability",
    cardName: "Accountability",
    tab: "governance",
    fields: [
      { fieldKey: "benchmarkExclusion", label: "PAI benchmark exclusion (Yes/No)", isBasic: true, cardId: "accountability", checkType: "boolean" },
      // Optional fields
      { fieldKey: "sustainabilityOwnerRole", label: "Sustainability owner role", isBasic: false, cardId: "accountability", checkType: "text" },
      { fieldKey: "controversialSectorsRevenue.controversialWeapons.amount", label: "Controversial weapons revenue", isBasic: false, cardId: "accountability", checkType: "numeric" },
      { fieldKey: "controversialSectorsRevenue.tobacco.amount", label: "Tobacco revenue", isBasic: false, cardId: "accountability", checkType: "numeric" },
      { fieldKey: "controversialSectorsRevenue.fossilFuels.amount", label: "Fossil fuels revenue", isBasic: false, cardId: "accountability", checkType: "numeric" },
      { fieldKey: "controversialSectorsRevenue.pesticides.amount", label: "Pesticides/agrochemicals revenue", isBasic: false, cardId: "accountability", checkType: "numeric" },
    ],
  },

  // ==================== SUSTAINABILITY STORY TAB ====================
  {
    cardId: "storyGeneral",
    cardName: "General (Sustainability Story)",
    tab: "sustainability-story",
    fields: [
      // Cover Page
      { fieldKey: "coverImageUrl", label: "Cover picture", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      // Leadership Statement
      { fieldKey: "leadershipSignedBy", label: "Leadership signed by (name)", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "leadershipTitle", label: "Leadership title", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "leadershipSignatureDate", label: "Date of signature", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "leadershipPhotoUrl", label: "Leadership photo", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      // B1 Basis for Preparation
      { fieldKey: "basisReportScope", label: "Report scope description", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "basisLimitations", label: "Limitations and improvements", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "basisCertifications", label: "Certifications relevance", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      // B2 & C2 Practices
      { fieldKey: "practicesSustainabilityManagement", label: "Sustainability management description", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "practicesConcretePractices", label: "Concrete practices", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "practicesTopInitiatives", label: "Top initiatives for next year", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      // C1 Strategy
      { fieldKey: "strategyHotspots", label: "Top 3 sustainability hotspots", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "strategyGoals", label: "2-3 year sustainability goals", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "strategyPriorities", label: "Top 3 priorities (12-24 months)", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "strategyFlagshipInitiatives", label: "Flagship initiatives", isBasic: false, cardId: "storyGeneral", checkType: "text" },
      { fieldKey: "strategyChallenges", label: "Biggest challenge/trade-off", isBasic: false, cardId: "storyGeneral", checkType: "text" },
    ],
  },
  {
    cardId: "storyEnvironment",
    cardName: "Environment (Sustainability Story)",
    tab: "sustainability-story",
    fields: [
      // B3 Energy & GHG emissions
      { fieldKey: "b3EmissionsDrivers", label: "Main drivers of Scope 1 & 2 emissions", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b3ReductionActions", label: "Actions taken to reduce energy/emissions", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b3NextPriorities", label: "Next priorities and targets (12-24 months)", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b3DataLimitations", label: "Data limitations and improvements", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      // B4 Pollution
      { fieldKey: "b4MonitoringProcess", label: "Pollution monitoring process", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b4PlannedImprovements", label: "Planned improvements to reduce pollution", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      // B5 Biodiversity
      { fieldKey: "b5MaterialityExplanation", label: "Biodiversity materiality explanation", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b5ValueChainImpacts", label: "Value chain biodiversity impacts", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b5ActionsToReduce", label: "Actions to reduce biodiversity impacts", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      // B6 Water
      { fieldKey: "b6WaterUseDescription", label: "Water use description", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b6WaterIssues", label: "Water-related issues", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b6WaterImprovements", label: "Water management improvements", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      // B7 Circular economy & waste
      { fieldKey: "b7WasteManagement", label: "Waste management description", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b7HazardousWaste", label: "Hazardous waste handling", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "b7CircularityImprovements", label: "Circularity improvements planned", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      // C3 Targets & transition actions
      { fieldKey: "c3Targets", label: "Sustainability targets (up to 3)", isBasic: false, cardId: "storyEnvironment", checkType: "array" },
      // C4 Climate risks
      { fieldKey: "c4PhysicalRisks", label: "Physical climate risks", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "c4TransitionRisks", label: "Transition risks and opportunities", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "c4RiskAssessment", label: "Risk assessment and management", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
      { fieldKey: "c4AdaptationActions", label: "Adaptation actions", isBasic: false, cardId: "storyEnvironment", checkType: "text" },
    ],
  },
  {
    cardId: "storySocial",
    cardName: "Social (Sustainability Story)",
    tab: "sustainability-story",
    fields: [
      // B8 Workforce
      { fieldKey: "b8WorkforceProfile", label: "Workforce profile and employment approach", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b8WorkforcePriorities", label: "Main workforce priorities this year", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b8EmployeeSupportActions", label: "Actions to support employees", isBasic: false, cardId: "storySocial", checkType: "text" },
      // B9 Health & Safety
      { fieldKey: "b9HealthSafetyApproach", label: "Health & safety approach and risks", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b9PreventionMeasures", label: "Incident prevention measures", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b9IssuesImprovements", label: "Notable H&S issues or improvements", isBasic: false, cardId: "storySocial", checkType: "text" },
      // B10 Remuneration/Training
      { fieldKey: "b10FairPayTrainingApproach", label: "Fair pay and training approach", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b10TrainingPriorities", label: "Training topics prioritized", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "b10NextYearImprovements", label: "Improvements for next year", isBasic: false, cardId: "storySocial", checkType: "text" },
      // C5 Additional Workforce & Diversity
      { fieldKey: "c5DiversityInclusionPolicies", label: "Diversity & inclusion policies", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "c5ContractorReliance", label: "Contractor/temporary/outsourced workers", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "c5ContractorManagement", label: "Contractor safety and fairness", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "c5DataLimitations", label: "Limitations in visibility/data", isBasic: false, cardId: "storySocial", checkType: "text" },
      // C6 Human Rights Policies
      { fieldKey: "c6HumanRightsApproach", label: "Human rights approach", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "c6HumanRightsRisks", label: "Most relevant human rights risks", isBasic: false, cardId: "storySocial", checkType: "text" },
      { fieldKey: "c6ControlsInPlace", label: "Controls in place", isBasic: false, cardId: "storySocial", checkType: "text" },
      // C7 Severe Incidents (conditional fields - narratives only shown if incidents exist)
      { fieldKey: "c7WorkforceIncidentsNarrative", label: "Workforce incidents narrative", isBasic: false, cardId: "storySocial", checkType: "text", conditionalOn: { field: "workforceIncidents", value: true } },
      { fieldKey: "c7ValueChainIncidentsNarrative", label: "Value chain incidents narrative", isBasic: false, cardId: "storySocial", checkType: "text", conditionalOn: { field: "valueChainIncidents", value: true } },
    ],
  },
  {
    cardId: "storyGovernance",
    cardName: "Governance (Sustainability Story)",
    tab: "sustainability-story",
    fields: [
      // B11 Business Conduct
      { fieldKey: "b11BusinessConductApproach", label: "Ethical business conduct approach", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "b11MisconductPrevention", label: "Misconduct prevention mechanisms", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "b11NotableUpdates", label: "Notable updates to strengthen business conduct", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      // C8 Sensitive Sectors / Exclusions
      { fieldKey: "c8SensitiveSectors", label: "Sensitive sectors description", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "c8Exclusions", label: "Exclusions or restrictions applied", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "c8ExclusionsPractice", label: "How exclusions are applied in practice", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      // C9 Governance Diversity
      { fieldKey: "c9GovernanceStructure", label: "Governance structure and decision-making", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "c9DiversityInclusion", label: "Diversity in governance/leadership", isBasic: false, cardId: "storyGovernance", checkType: "text" },
      { fieldKey: "c9ImprovementsGoals", label: "Governance diversity improvements/goals", isBasic: false, cardId: "storyGovernance", checkType: "text" },
    ],
  },
];

// ---------------------------------------------------------------------------
// Generic completion calculator (usable in any service)
// ---------------------------------------------------------------------------

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((acc: any, key: string) => acc?.[key], obj);
}

export function isFieldFilled(field: FieldDefinition, data: any): boolean {
  switch (field.checkType) {
    case 'numeric': {
      const v = getNestedValue(data, field.fieldKey);
      if (v === null || v === undefined) return false;
      // Frontend stores numbers as strings ("") before the user types
      if (typeof v === 'string') {
        const trimmed = v.trim();
        return trimmed !== '' && !isNaN(parseFloat(trimmed));
      }
      return typeof v === 'number';
    }
    case 'text': {
      const v = getNestedValue(data, field.fieldKey);
      return typeof v === 'string' && v.trim().length > 0;
    }
    case 'boolean':
      return typeof getNestedValue(data, field.fieldKey) === 'boolean';
    case 'array': {
      const v = getNestedValue(data, field.fieldKey);
      return Array.isArray(v) && v.length > 0;
    }
    case 'arrayOrFields':
      // True if ANY array property in the object is non-empty (e.g. carbonEmissions)
      return Object.values(data || {}).some(v => Array.isArray(v) && (v as any[]).length > 0);
    case 'primarySiteAddress': {
      // siteAddresses[0].address must be a non-empty string
      const sites = getNestedValue(data, field.fieldKey);
      if (!Array.isArray(sites) || sites.length === 0) return false;
      return typeof sites[0]?.address === 'string' && sites[0].address.trim().length > 0;
    }
    default: {
      const v = getNestedValue(data, field.fieldKey);
      return v !== null && v !== undefined;
    }
  }
}

/**
 * Generic completion calculator driven by FIELD_DEFINITIONS.
 * Counts only basic (required) fields. Conditional fields are skipped
 * unless their condition is met.
 */
export function calculateCardCompletion(cardId: string, data: any): number {
  if (!data) return 0;

  const card = FIELD_DEFINITIONS.find(c => c.cardId === cardId);
  if (!card) return 0;

  const basicFields = card.fields.filter(f => f.isBasic);
  if (basicFields.length === 0) return 100;

  let required = 0;
  let completed = 0;

  for (const field of basicFields) {
    if (field.conditionalOn) {
      const actual = getNestedValue(data, field.conditionalOn.field);
      if (actual !== field.conditionalOn.value) continue; // not applicable
    }
    required++;
    if (isFieldFilled(field, data)) completed++;
  }

  if (required === 0) return 100;
  return Math.round((completed / required) * 100);
}

// ---------------------------------------------------------------------------

/**
 * Helper to get all basic (required) fields
 */
export function getBasicFields(): FieldDefinition[] {
  return FIELD_DEFINITIONS.flatMap(card => card.fields.filter(f => f.isBasic));
}

/**
 * Helper to get all comprehensive (optional) fields
 */
export function getComprehensiveOnlyFields(): FieldDefinition[] {
  return FIELD_DEFINITIONS.flatMap(card => card.fields.filter(f => !f.isBasic));
}

/**
 * Helper to get fields by card
 */
export function getFieldsByCard(cardId: string): FieldDefinition[] {
  const card = FIELD_DEFINITIONS.find(c => c.cardId === cardId);
  return card?.fields || [];
}

/**
 * Helper to get cards by tab
 */
export function getCardsByTab(tab: TabName): CardDefinition[] {
  return FIELD_DEFINITIONS.filter(card => card.tab === tab);
}
